import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsync, useAsyncFn } from "react-use";
import { useApi } from "../Api/ApiContext";
import CurrencyConverter from "../utils/CurrencyConverter";
import Switch from "../Switch";
import {
  Alert,
  Badge,
  Card,
  CardText,
  CardTitle,
  Col,
  Form,
  Label,
  Row,
  Spinner,
  Button,
  FormGroup,
  FormFeedback,
} from "reactstrap";
import Layout, { Main } from "../Layout";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Input from "../component/Input";
import Textarea from "../component/Textarea";
import { Price } from "./Celebrity";

const BadgeWrapper = styled.div`
  cursor: pointer;
`;
const Booking = ({ celebrities, orderData, setOrderData }) => {
  const { getOrderType, createOrder, updateOrder } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedType, setSelectedType] = useState();
  const [isEuro, setIsEuro] = useState(
    location.state?.currency === "euro"
      ? true
      : location.state?.currency !== "cfa"
  );

  const { loading, error, value: orderTypes } = useAsync(() => getOrderType());

  const [
    { loading: updateLoading, error: updateError, value: updateResponse },
    makeUpdateOrder,
  ] = useAsyncFn((orderParams, orderRef) => updateOrder(orderParams, orderRef));

  const [
    { loading: orderLoading, error: orderError, value: orderResponse },
    makeCreateOrder,
  ] = useAsyncFn((orderParams) => createOrder(orderParams));

  const {
    register,
    formState: { errors },
    handleSubmit,
    values,
    watch,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: {
      event: orderData?.order?.event,
      dedicaceFrom: orderData?.order?.dedicaceFrom,
      dedicaceTarget: orderData?.order?.dedicaceTarget,
      comment: orderData?.order?.comment,
      deliveryEmail: orderData?.order?.deliveryEmail,
      dedicaceTo: orderData?.order?.dedicaceTo,
    },
  });

  const product = location.state?.product || orderData.product;
  const celebrity = location.state?.celebrity || orderData.celebrity;

  useEffect(() => {
    if (product.category.slug !== "dedicace-video") {
      setValue("event", product.category.slug);
    }
  }, [product.category.slug]);

  register("event", {
    required:
      product.category.slug === "dedicace-video"
        ? "Vous devez sélectionner le motif de la commande."
        : false,
  });

  const dedicaceTarget = watch("dedicaceTarget");

  const handleTypeSelected = (e, orderType) => {
    e.preventDefault();
    if (orderType.title.toLowerCase() === orderData?.order?.event) {
      setOrderData({
        ...orderData,
        order: { ...orderData.order, event: undefined },
      });
      return;
    }
    if (orderType === selectedType) {
      setValue("event", undefined);
      setSelectedType(null);
      return;
    }
    clearErrors("event");
    setSelectedType(orderType);
    setValue("event", orderType.title.toLowerCase());
  };

  const onSubmit = (data) => {
    const orderParams = {
      ...data,
      celebrityId: celebrity.id,
      productId: product.id,
      dedicaceTo:
        data.dedicaceTarget === "me" ? data.dedicaceFrom : data.dedicaceTo,
    };

    setOrder(orderParams);

    setOrderData({
      celebrity: celebrity,
      product: product,
      order: orderParams,
    });

    if (orderData?.orderResponse) {
      makeUpdateOrder(orderParams, orderData?.orderResponse.ref);
      return;
    }

    makeCreateOrder(orderParams);
  };

  if (!product) {
    navigate("/");
  }
  if (loading || orderLoading || updateLoading) {
    return (
      <Layout celebrities={celebrities}>
        <Main>
          <Row>
            <Col lg={6} className={"mx-auto"}>
              <Spinner color={"danger"} />
            </Col>
          </Row>
        </Main>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout celebrities={celebrities}>
        <Main>
          <Row>
            <Col lg={6} className="mx-auto">
              <Alert color={"danger"}>
                Une erreur s'est produite. Merci de réessayer plus tard.
              </Alert>
            </Col>
          </Row>
        </Main>
      </Layout>
    );
  }

  if (orderResponse || updateResponse) {
    setOrderData({
      ...orderData,
      orderResponse: orderResponse || updateResponse,
    });
    navigate(`/shop/${celebrity.id}/order-summary`, {
      state: {
        isEuro: isEuro,
      },
    });
  }

  return (
    <Layout
      celebrities={celebrities}
      backPath={{
        pathname: `/shop/${celebrity.id}/infos`,
        state: { celebrity: celebrity, currency: isEuro ? "euro" : "cfa" },
      }}
    >
      <Main className="my-lg-3 my-3">
        {(orderError || updateError) && (
          <Row>
            <Col lg={6} className={"mx-auto"}>
              <Alert color="danger">
                Une erreur s'est produite lors de la commande. Merci de
                réessayer plus tard.
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col lg={6} className={"mx-auto"}>
            <h1 className={"fs-4"}>Compléter votre commande</h1>
          </Col>
        </Row>
        <Row className={"mt-lg-2 mt-2"}>
          <Col lg={6} className="mx-auto">
            <Card body>
              <CardTitle tag="h6" className={"fw-bold text-capitalize"}>
                {product.category.name}
              </CardTitle>
              <CardText>
                {product.category.slug.toLowerCase() !== "dedicace-musicale" ? (
                  <p>
                    Livré en :{" "}
                    <span className={"fw-bold"}>{celebrity.delay} heures</span>
                  </p>
                ) : (
                  <p>
                    Date indicative de sortie de la chanson :{" "}
                    <span className={"fw-bold"}>
                      {new Date(product.launchDate).toLocaleDateString()}
                    </span>
                  </p>
                )}
                <Row className={"d-flex align-items-baseline"}>
                  <Col sm={1}>
                    <Switch
                      state={isEuro}
                      setState={setIsEuro}
                      trueLabel={"EURO"}
                      falseLabel={"CFA"}
                    />
                  </Col>
                  <Col sm={{ size: 3, offset: 4 }}>
                    <Price className={"fw-bold text-center fs-4"}>
                      {CurrencyConverter(
                        product.price,
                        product.currency,
                        isEuro ? "euro" : "cfa"
                      )}
                    </Price>
                  </Col>
                </Row>
              </CardText>
            </Card>
          </Col>
        </Row>
        <Row className={"mt-4"}>
          <Col lg={6} className="mx-auto">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <Label htmlFor="dedicaceFrom">Votre nom et prénom</Label>
                <Input
                  className={
                    errors.dedicaceFrom
                      ? "is-invalid form-control"
                      : "form-control"
                  }
                  id={"dedicaceFrom"}
                  type={"text"}
                  name={"dedicaceFrom"}
                  value={values?.dedicaceFrom}
                  {...register("dedicaceFrom", {
                    required: "Ce champ est obligatoire.",
                  })}
                />
                {errors.dedicaceFrom && (
                  <FormFeedback>{errors.dedicaceFrom.message}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>A qui est destinée cette dédicace ?</Label>
                <FormGroup row>
                  <Col xs={1}>
                    <Input
                      className={errors.dedicaceTarget && "is-invalid"}
                      id={"me"}
                      name={"dedicaceTarget"}
                      type={"radio"}
                      value="me"
                      {...register("dedicaceTarget", {
                        required: "Ce champ est obligatoire.",
                      })}
                    />
                  </Col>
                  <Col>
                    <Label for={"me"}>Moi même</Label>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col xs={1}>
                    <Input
                      className={errors.dedicaceTarget && "is-invalid"}
                      id={"other"}
                      name={"dedicaceTarget"}
                      type={"radio"}
                      value="other"
                      {...register("dedicaceTarget", {
                        required: "Ce champ est obligatoire.",
                      })}
                    />
                  </Col>
                  <Col>
                    <Label htmlFor={"other"}>Une autre personne</Label>
                  </Col>
                </FormGroup>
                {errors.dedicaceTarget && (
                  <div>
                    <small className={"text-danger"}>
                      {errors.dedicaceTarget.message}
                    </small>
                  </div>
                )}
              </FormGroup>
              {dedicaceTarget === "other" && (
                <FormGroup>
                  <Label htmlFor={"to"}>
                    Nom et prénom du bénéficiaire de la dédicace
                  </Label>
                  <Input
                    className={
                      errors.dedicaceTo
                        ? "is-invalid form-control"
                        : "form-control"
                    }
                    id="to"
                    type={"text"}
                    name={"dedicaceTo"}
                    value={values?.dedicaceTo}
                    {...register("dedicaceTo")}
                  />
                </FormGroup>
              )}
              {product.category.slug === "dedicace-video" && (
                <FormGroup>
                  <Label>
                    Sélectionnez ci-dessous le motif de votre commande
                  </Label>

                  <Row>
                    {orderTypes.map((orderType) => (
                      <Col xs={12} md={6}>
                        <BadgeWrapper>
                          <Badge
                            key={orderType.id}
                            className={`w-100 py-3 fs-6 mb-2 border border-secondary-subtle ${
                              selectedType === orderType ||
                              order?.event === orderType.title.toLowerCase() ||
                              orderData?.order?.event ===
                                orderType.title.toLowerCase()
                                ? "text-white"
                                : "text-secondary"
                            }`}
                            color={
                              selectedType === orderType ||
                              order?.event === orderType.title.toLowerCase() ||
                              orderData?.order?.event ===
                                orderType.title.toLowerCase()
                                ? "success"
                                : "light"
                            }
                            onClick={(e) => handleTypeSelected(e, orderType)}
                            pill
                          >
                            {orderType.title}
                          </Badge>
                        </BadgeWrapper>
                      </Col>
                    ))}
                  </Row>
                </FormGroup>
              )}
              <FormGroup>
                <Label>
                  Expliquez à votre célébrité ce que vous attendez de cette
                  dédicace.
                </Label>
                <Textarea
                  className={
                    errors.comment ? "is-invalid form-control" : "form-control"
                  }
                  id={"comment"}
                  name={"comment"}
                  rows={5}
                  {...register("comment", {
                    required: "Ce champ est obligatoire.",
                    maxLength: {
                      value: 500,
                      message: "Le nombre de caractères maximum est de 500.",
                    },
                  })}
                  placeholder={selectedType?.example}
                >
                  {values?.comment}
                </Textarea>
                {errors.comment && (
                  <FormFeedback>{errors.comment.message}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label htmlFor={"email"}>Votre adresse email</Label>
                <Input
                  className={
                    errors.deliveryEmail
                      ? "is-invalid form-control"
                      : "form-control"
                  }
                  id={"email"}
                  value={values?.deliveryEmail}
                  name={"deliveryEmail"}
                  {...register("deliveryEmail", {
                    required: "Ce champ est obligatoire.",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message:
                        "Le format de l'adresse e-mail n'est pas valide.",
                    },
                  })}
                />
                {errors.deliveryEmail && (
                  <FormFeedback>{errors.deliveryEmail.message}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup
                className={"justify-content-sm-end justify-content-around"}
                row
              >
                <Col xs={12} md={4}>
                  <Button color="primary" className={"w-100"} type={"submit"}>
                    Valider
                  </Button>
                </Col>
                {errors.event && (
                  <div>
                    <small className={"text-danger"}>
                      {errors.event.message}
                    </small>
                  </div>
                )}
              </FormGroup>
            </Form>
          </Col>
        </Row>
      </Main>
    </Layout>
  );
};

export default Booking;
